import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

interface FAQItem {
  id: string;
  category: string;
  question: string;
  answer: string;
}

export default function ContactFAQ() {
  const { t } = useLanguage();
  const [openId, setOpenId] = useState<string | null>('faq-1');
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const categories = [
    { id: 'all', label: t('全部', 'All', 'すべて') },
    { id: 'product', label: t('產品與材料', 'Products & Materials', '製品・材料') },
    { id: 'cooperation', label: t('合作方式', 'Cooperation', '協力方法') },
    { id: 'process', label: t('導入流程', 'Process', '導入プロセス') },
    { id: 'esg', label: t('ESG / 低碳', 'ESG / Low-Carbon', 'ESG・低炭素') },
  ];

  const faqs: FAQItem[] = [
    {
      id: 'faq-1',
      category: 'product',
      question: t(
        'PQernel® 和 PQMB 有什麼不同？適合哪些應用？',
        'What is the difference between PQernel® and PQMB? Which applications are they suitable for?',
        'PQernel®とPQMBの違いは何ですか？どのような用途に適していますか？'
      ),
      answer: t(
        'PQernel® 是聚量的核心相容劑技術，專為提升再生塑膠的物性（拉伸強度、衝擊韌性、加工穩定性）而設計，適用於造粒廠、射出押出製造商，可大幅提升再生料的可用性與品質。PQMB 則是多功能母粒系列，針對特定功能需求（如抗菌、抗靜電、阻燃、UV 穩定）提供客製化解決方案，適合品牌端與製造商在產品差異化上的需求。兩者可搭配使用，也可單獨導入。',
        'PQernel® is PolyQ\'s core compatibilizer technology, designed to enhance the mechanical properties of recycled plastics (tensile strength, impact toughness, processing stability). It is ideal for compounders and injection/extrusion manufacturers to significantly improve the usability and quality of recycled materials. PQMB is a multifunctional masterbatch series that provides customized solutions for specific functional requirements (such as antibacterial, antistatic, flame retardant, UV stabilization), suitable for brand owners and manufacturers seeking product differentiation. Both can be used together or independently.',
        'PQernel®は聚量のコア相溶化剤技術で、再生プラスチックの物性（引張強度・衝撃靭性・加工安定性）向上のために設計されており、造粒業者や射出押出メーカーに最適です。PQMBは多機能マスターバッチシリーズで、特定の機能要件（抗菌・帯電防止・難燃・UV安定化など）に対するカスタマイズソリューションを提供し、製品差別化を求めるブランドオーナーやメーカーに適しています。両製品は組み合わせて使用することも、単独で導入することも可能です。'
      ),
    },
    {
      id: 'faq-2',
      category: 'product',
      question: t(
        '聚量的材料可以用在哪些塑膠種類？支援 rPET、rPP、rPE 嗎？',
        'Which plastic types are compatible with PolyQ materials? Do they support rPET, rPP, rPE?',
        '聚量の材料はどのプラスチック種類に対応していますか？rPET・rPP・rPEに対応していますか？'
      ),
      answer: t(
        '聚量的技術支援廣泛的塑膠材料，包括 rPET、rPP、rPE、ABS、PU、TPE 及混合塑料。針對不同材料組合，我們會進行配方評估與試驗，確認最佳添加比例與製程條件。如果您有特定材料需求，歡迎在諮詢表單中說明，我們會提供針對性的技術建議。',
        'PolyQ\'s technology supports a wide range of plastic materials, including rPET, rPP, rPE, ABS, PU, TPE, and mixed plastics. For different material combinations, we conduct formulation assessments and trials to confirm optimal addition ratios and processing conditions. If you have specific material requirements, please describe them in the inquiry form and we will provide targeted technical recommendations.',
        '聚量の技術はrPET・rPP・rPE・ABS・PU・TPEおよび混合プラスチックを含む幅広いプラスチック材料に対応しています。異なる材料の組み合わせに対して、配合評価と試験を実施し、最適な添加比率と加工条件を確認します。特定の材料要件がある場合は、お問い合わせフォームにご記入ください。'
      ),
    },
    {
      id: 'faq-3',
      category: 'cooperation',
      question: t(
        '我是小型製造商，最低採購量是多少？可以先試用嗎？',
        'I am a small manufacturer. What is the minimum order quantity? Can I try a sample first?',
        '私は小規模メーカーです。最小注文数量はいくらですか？まずサンプルを試すことはできますか？'
      ),
      answer: t(
        '聚量歡迎各規模的合作夥伴，包括小型製造商與新創品牌。我們提供試產驗證服務，可先以小批量進行配方測試，確認效果後再討論正式採購規模。最低採購量依產品類型而異，建議先透過諮詢表單說明您的需求，我們的技術團隊會與您討論最適合的合作方式。',
        'PolyQ welcomes partners of all sizes, including small manufacturers and emerging brands. We offer pilot production and validation services, allowing small-batch formulation testing before discussing full-scale procurement. Minimum order quantities vary by product type. We recommend submitting an inquiry form to describe your needs, and our technical team will discuss the most suitable cooperation approach with you.',
        '聚量はスモールメーカーや新興ブランドを含むあらゆる規模のパートナーを歓迎します。試験生産・検証サービスを提供しており、小ロットでの配合テストを行ってから本格的な調達規模を検討することができます。最小注文数量は製品タイプによって異なります。お問い合わせフォームでニーズをお知らせいただければ、技術チームが最適な協力方法をご提案します。'
      ),
    },
    {
      id: 'faq-4',
      category: 'cooperation',
      question: t(
        '聚量有提供海外經銷合作嗎？如何成為經銷商？',
        'Does PolyQ offer overseas distribution partnerships? How can I become a distributor?',
        '聚量は海外販売代理協力を提供していますか？どのようにして販売代理店になれますか？'
      ),
      answer: t(
        '是的，聚量積極拓展海外市場，目前開放日本、東南亞、歐洲等地區的經銷合作申請。成為經銷商的流程包括：提交合作申請 → 市場評估與洽談 → 簽訂經銷協議 → 技術培訓與支援 → 正式上市。有意成為經銷商的夥伴，請在諮詢表單中選擇「區域經銷合作」並說明您的市場範圍與背景，我們會優先安排洽談。',
        'Yes, PolyQ is actively expanding overseas markets and currently accepts distribution partnership applications in Japan, Southeast Asia, Europe, and other regions. The process to become a distributor includes: Submit application → Market assessment and negotiation → Sign distribution agreement → Technical training and support → Official launch. Partners interested in becoming distributors should select "Regional Distribution Partnership" in the inquiry form and describe your market scope and background. We will prioritize scheduling a meeting.',
        'はい、聚量は積極的に海外市場を拡大しており、現在日本・東南アジア・ヨーロッパなどの地域での販売代理協力申請を受け付けています。販売代理店になるプロセスは：申請提出→市場評価・交渉→販売代理契約締結→技術研修・サポート→正式ローンチです。販売代理店に興味のあるパートナーは、お問い合わせフォームで「地域販売代理協力」を選択し、市場範囲と背景をご記入ください。'
      ),
    },
    {
      id: 'faq-5',
      category: 'process',
      question: t(
        '從送出諮詢到開始試產，大概需要多久時間？',
        'How long does it take from submitting an inquiry to starting pilot production?',
        'お問い合わせ送信から試験生産開始まで、どのくらいの時間がかかりますか？'
      ),
      answer: t(
        '一般流程如下：送出諮詢後 3 個工作天內回覆 → 需求確認與技術評估（約 1–2 週）→ 配方設計與樣品製備（約 2–4 週）→ 試產驗證（依規模 2–6 週）→ 結果回報與調整。整體從諮詢到完成試產驗證，通常約 6–12 週，視材料複雜度與製程條件而定。緊急需求可在諮詢時說明，我們會盡力配合。',
        'The general process is as follows: Reply within 3 business days after inquiry → Needs confirmation and technical assessment (approx. 1–2 weeks) → Formulation design and sample preparation (approx. 2–4 weeks) → Pilot production and validation (2–6 weeks depending on scale) → Results reporting and adjustment. Overall, from inquiry to completing pilot production validation typically takes about 6–12 weeks, depending on material complexity and processing conditions. Urgent requirements can be noted in the inquiry and we will do our best to accommodate.',
        '一般的なプロセスは以下の通りです：お問い合わせ後3営業日以内に返信→ニーズ確認・技術評価（約1〜2週間）→配合設計・サンプル準備（約2〜4週間）→試験生産・検証（規模により2〜6週間）→結果報告・調整。お問い合わせから試験生産検証完了まで、通常約6〜12週間かかります（材料の複雑さと加工条件による）。緊急の要件はお問い合わせ時にお知らせください。'
      ),
    },
    {
      id: 'faq-6',
      category: 'process',
      question: t(
        '聯絡前需要準備什麼資料？沒有詳細資料也可以諮詢嗎？',
        'What information should I prepare before contacting? Can I inquire without detailed information?',
        '連絡前にどのような資料を準備すればよいですか？詳細な資料がなくてもお問い合わせできますか？'
      ),
      answer: t(
        '完全可以！即使沒有詳細資料，也歡迎先送出諮詢。如果能提供以下資訊會更有幫助：目前使用的塑膠材料種類（如 rPET、rPP）、製程類型（射出、押出、造粒等）、希望改善的問題（物性、加工穩定性、成本等）、預計應用場景。但如果您只是想初步了解聚量的技術或產品，直接說明您的行業背景與需求方向即可，我們的技術團隊會協助您釐清需求。',
        'Absolutely! Even without detailed information, you are welcome to submit an inquiry first. The following information would be helpful if available: current plastic material types (e.g. rPET, rPP), process type (injection, extrusion, compounding, etc.), issues to improve (mechanical properties, processing stability, cost, etc.), and intended application scenarios. However, if you simply want to learn about PolyQ\'s technology or products, just describe your industry background and general needs — our technical team will help clarify your requirements.',
        'もちろんです！詳細な資料がなくても、まずお問い合わせいただけます。以下の情報があると助かります：現在使用しているプラスチック材料の種類（rPET・rPPなど）、製造工程の種類（射出・押出・造粒など）、改善したい課題（物性・加工安定性・コストなど）、想定される用途。ただし、聚量の技術や製品について初めて知りたい場合は、業界背景と大まかなニーズをお知らせいただければ、技術チームが要件の整理をお手伝いします。'
      ),
    },
    {
      id: 'faq-7',
      category: 'esg',
      question: t(
        '使用聚量的材料可以取得碳足跡或 ESG 相關認證嗎？',
        'Can I obtain carbon footprint or ESG-related certifications by using PolyQ materials?',
        '聚量の材料を使用することで、カーボンフットプリントやESG関連の認証を取得できますか？'
      ),
      answer: t(
        '聚量的材料技術可協助您提升產品中的再生料比例，進而降低碳足跡並支援 ESG 報告。我們可提供材料的再生料含量數據、加工過程的碳排放估算，以及符合 GRS（全球再生標準）的材料來源說明。如需配合特定認證（如 ISO 14067、EPD 環境產品聲明），建議在諮詢時說明，我們會協助評估可行性並提供相關技術文件支援。',
        'PolyQ\'s material technology can help you increase the recycled content ratio in your products, thereby reducing carbon footprint and supporting ESG reporting. We can provide recycled content data for materials, carbon emission estimates for processing, and material source documentation compliant with GRS (Global Recycled Standard). If you need to meet specific certifications (such as ISO 14067 or EPD Environmental Product Declaration), please mention this during your inquiry and we will help assess feasibility and provide relevant technical documentation support.',
        '聚量の材料技術は製品中の再生材料比率向上を支援し、カーボンフットプリントの削減とESGレポートをサポートします。材料の再生材料含有量データ、加工プロセスの炭素排出量推定、GRS（グローバルリサイクルスタンダード）準拠の材料源説明を提供できます。特定の認証（ISO 14067やEPD環境製品宣言など）が必要な場合は、お問い合わせ時にお知らせください。'
      ),
    },
    {
      id: 'faq-8',
      category: 'esg',
      question: t(
        '政府單位或示範場域可以與聚量合作嗎？',
        'Can government agencies or demonstration sites cooperate with PolyQ?',
        '政府機関や実証サイトは聚量と協力できますか？'
      ),
      answer: t(
        '非常歡迎！聚量積極參與政府主導的循環經濟示範計畫、低碳材料推廣專案與 ESG 政策落地合作。我們可提供技術支援、材料供應、數據報告與成果展示等服務。如果您代表政府單位、研究機構或示範場域，請在諮詢表單中選擇「政府 / 示範場域合作」，並說明計畫背景與目標，我們會安排專人對接。',
        'Very welcome! PolyQ actively participates in government-led circular economy demonstration projects, low-carbon material promotion programs, and ESG policy implementation partnerships. We can provide technical support, material supply, data reporting, and results presentation services. If you represent a government agency, research institution, or demonstration site, please select "Government / Demonstration Site Cooperation" in the inquiry form and describe the project background and objectives. We will arrange a dedicated contact person.',
        '大歓迎です！聚量は政府主導のサーキュラーエコノミー実証プロジェクト・低炭素材料普及プログラム・ESG政策実施協力に積極的に参加しています。技術サポート・材料供給・データレポート・成果発表などのサービスを提供できます。政府機関・研究機関・実証サイトを代表される場合は、お問い合わせフォームで「政府・実証サイト協力」を選択し、プロジェクトの背景と目標をご記入ください。'
      ),
    },
  ];

  const filtered = activeCategory === 'all' ? faqs : faqs.filter((f) => f.category === activeCategory);

  const toggle = (id: string) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  const scrollToForm = () => {
    const el = document.getElementById('contact-form');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className="section-padding bg-white">
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="max-w-2xl mb-10">
          <p className="text-[0.9375rem] font-semibold text-brand-green uppercase tracking-widest mb-2">
            {t('常見問題', 'FAQ', 'よくある質問')}
          </p>
          <h2
            className="font-bold text-neutral-dark mb-3"
            style={{ fontSize: 'clamp(1.375rem, 2.5vw, 1.875rem)', lineHeight: '1.35' }}
          >
            {t(
              '您可能想先了解的問題',
              'Questions You May Want to Know First',
              'まず知りたいかもしれない質問'
            )}
          </h2>
          <p className="text-neutral-mid text-[1rem] leading-relaxed">
            {t(
              '整理了最常見的諮詢問題，幫助您在聯絡前先找到答案。若仍有疑問，歡迎直接送出諮詢。',
              'We\'ve compiled the most frequently asked questions to help you find answers before reaching out. If you still have questions, feel free to submit an inquiry.',
              'お問い合わせ前に答えを見つけていただけるよう、最もよくある質問をまとめました。それでもご不明な点がある場合は、お気軽にお問い合わせください。'
            )}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-10 items-start">
          {/* Left: category filter */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-1.5">
              <p className="text-[0.75rem] font-semibold text-neutral-mid uppercase tracking-wider mb-3 px-1">
                {t('問題分類', 'Categories', 'カテゴリー')}
              </p>
              {categories.map((cat) => {
                const count = cat.id === 'all' ? faqs.length : faqs.filter((f) => f.category === cat.id).length;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => {
                      setActiveCategory(cat.id);
                      setOpenId(null);
                    }}
                    className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-[0.9375rem] font-medium transition-all duration-200 cursor-pointer whitespace-nowrap text-left ${
                      activeCategory === cat.id
                        ? 'bg-brand-green text-white'
                        : 'text-neutral-dark hover:bg-[#F5F8FB] hover:text-brand-green'
                    }`}
                  >
                    <span>{cat.label}</span>
                    <span
                      className={`text-[0.75rem] font-bold px-2 py-0.5 rounded-full ${
                        activeCategory === cat.id
                          ? 'bg-white/25 text-white'
                          : 'bg-neutral-border text-neutral-mid'
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}

              {/* Still have questions? */}
              <div className="mt-6 p-4 rounded-xl bg-[#F5F8FB] border border-neutral-border">
                <p className="font-semibold text-neutral-dark text-[0.9375rem] mb-1.5">
                  {t('沒找到答案？', 'Didn\'t find your answer?', '答えが見つかりませんでしたか？')}
                </p>
                <p className="text-neutral-mid text-[0.875rem] leading-relaxed mb-3">
                  {t(
                    '直接送出諮詢，我們 3 個工作天內回覆。',
                    'Submit an inquiry directly. We reply within 3 business days.',
                    '直接お問い合わせください。3営業日以内にご返信します。'
                  )}
                </p>
                <button
                  type="button"
                  onClick={scrollToForm}
                  className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-brand-green text-white text-[0.875rem] font-semibold hover:bg-brand-green-dark transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-send-plane-line text-sm"></i>
                  {t('送出諮詢', 'Submit Inquiry', 'お問い合わせ')}
                </button>
              </div>
            </div>
          </div>

          {/* Right: FAQ accordion */}
          <div className="lg:col-span-3 space-y-3">
            {filtered.map((faq) => {
              const isOpen = openId === faq.id;
              return (
                <div
                  key={faq.id}
                  className={`rounded-xl border transition-all duration-200 overflow-hidden ${
                    isOpen
                      ? 'border-brand-green/40 bg-[#F5FBF8]'
                      : 'border-neutral-border bg-white hover:border-brand-green/25 hover:bg-[#FAFCFB]'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggle(faq.id)}
                    className="w-full flex items-start gap-4 px-6 py-5 text-left cursor-pointer"
                  >
                    <div
                      className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 transition-all duration-200 ${
                        isOpen ? 'bg-brand-green' : 'bg-[#F5F8FB] border border-neutral-border'
                      }`}
                    >
                      <i
                        className={`text-sm transition-transform duration-300 ${
                          isOpen
                            ? 'ri-subtract-line text-white'
                            : 'ri-add-line text-neutral-mid'
                        }`}
                      ></i>
                    </div>
                    <span
                      className={`font-semibold text-[1rem] leading-snug flex-1 transition-colors duration-200 ${
                        isOpen ? 'text-brand-green' : 'text-neutral-dark'
                      }`}
                    >
                      {faq.question}
                    </span>
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-6 pb-6 pl-16">
                      <p className="text-neutral-mid text-[0.9375rem] leading-[1.8]">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}

            {filtered.length === 0 && (
              <div className="text-center py-12 text-neutral-mid text-[1rem]">
                {t('此分類暫無問題', 'No questions in this category', 'このカテゴリーには質問がありません')}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
